import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

export const SITE_URL = "https://inzorah.com";
export const OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * One place for every on-page SEO / AEO signal.
 * Pass `jsonLd` as an object or array of objects.
 */
const Seo = ({
  title,
  description,
  path = "/",
  image = OG_IMAGE,
  type = "website",
  publishedTime,
  keywords,
  jsonLd,
  noindex = false,
  imageAlt,
}) => {
  const url = `${SITE_URL}${path}`;
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  // og:image must be absolute; blog heroes already are, local assets are not.
  const imageUrl = /^https?:\/\//.test(image) ? image : `${SITE_URL}${image}`;
  const isDefaultImage = imageUrl === OG_IMAGE;

  // public/index.html ships a baseline title/description/og set so crawlers that
  // do not run JS still get a preview. Under React 19 Helmet lets React hoist its
  // own tags instead of patching the DOM, so it cannot dedupe against static
  // markup - we clear the fallbacks ourselves once a real page has rendered.
  useEffect(() => {
    document
      .querySelectorAll('head [data-inz-seo="fallback"]')
      .forEach((node) => node.remove());
  }, []);

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex,nofollow"
            : "index,follow,max-image-preview:large,max-snippet:-1"
        }
      />
      <meta name="author" content="InZoRAH" />
      <meta name="publisher" content="KYSolutions LLC" />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="InZoRAH" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:alt" content={imageAlt || title} />
      {isDefaultImage && <meta property="og:image:width" content="1200" />}
      {isDefaultImage && <meta property="og:image:height" content="630" />}
      <meta property="og:locale" content="en_US" />
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt || title} />

      {blocks.map((block, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

/* ---------- reusable structured data ---------- */

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "InZoRAH",
  legalName: "KYSolutions LLC",
  url: SITE_URL,
  description:
    "InZoRAH is an ingredient checker for food, drink, supplement and skincare labels. It tells you what ingredients are in any product, in plain English, with real public sources.",
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "InZoRAH",
  url: SITE_URL,
  inLanguage: "en",
  publisher: { "@type": "Organization", name: "KYSolutions LLC" },
};

export const appLd = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "InZoRAH",
  applicationCategory: "HealthApplication",
  operatingSystem: "iOS, Android",
  url: SITE_URL,
  description:
    "A food ingredient scanner and ingredient analyzer in one: scan a barcode or ingredient label and see what is inside, explained in plain English with sources from USDA FoodData Central, openFDA and Open Food Facts.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free plan with 12 scans a month; Premium from $4.99/month.",
  },
  featureList: [
    "Barcode and ingredient label scanning",
    "Plain-English ingredient explanations",
    "Sourced claims from USDA, openFDA and Open Food Facts",
    "Allergy, diet and skin-concern personalisation",
    "Cleaner product swap suggestions",
  ],
  publisher: { "@type": "Organization", name: "KYSolutions LLC" },
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const faqLd = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export default Seo;
